import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useApolloClient, useMutation } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { AlertTriangle, CheckCircle2, Download, FileUp, FolderKanban, LoaderCircle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'
import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import { useUpload } from '../../../components/file-manager/hooks/useUpload'
import DocumentSelectorModal from '../../doc-control/components/DocumentSelectorModal'
import { costBudgetDownloadUrl } from '../lib/download'
import ImportErrorsTable from '../components/ImportErrorsTable'
import { describeCostControlError } from '../lib/errors'
import { MAX_IMPORT_BYTES, importDocumentPick, importFileProblem, type PickedDocument } from '../lib/importSource'
import type { CostImportResult } from '../types'

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

type Phase = 'pick' | 'uploading' | 'checking' | 'report' | 'applying' | 'done'

interface Props { open: boolean; onOpenChange: (open: boolean) => void; projectId: string; locked: boolean; canLinkDocuments: boolean }

const validateFile = (file: File, t: (key: string) => string): string | null => {
  const problem = importFileProblem(file.name, file.size)
  return problem ? t(problem) : null
}
const mimeOf = (file: File) => file.type || (file.name.toLowerCase().endsWith('.csv') ? 'text/csv' : XLSX_MIME)
const hasChanges = (r: CostImportResult) => r.groupsCreated + r.itemsCreated + r.itemsUpdated > 0

const ImportDialog: React.FC<Props> = ({ open, onOpenChange, projectId, locked, canLinkDocuments }) => {
  const { t } = useTranslation()
  const client = useApolloClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [fileId, setFileId] = useState<string | null>(null)
  const [doc, setDoc] = useState<{ revisionId: string; name: string; version: number | null } | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [phase, setPhase] = useState<Phase>('pick')
  const [error, setError] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [report, setReport] = useState<CostImportResult | null>(null)
  const [discarding, setDiscarding] = useState(false)

  const [createFile] = useMutation(Mutation.CREATE_FILE, { context: { handlesErrors: true } })
  const [setFileUploaded] = useMutation(Mutation.SET_FILE_UPLOADED, { context: { handlesErrors: true } })
  const [deleteFile] = useMutation(Mutation.DELETE_FILE, { context: { handlesErrors: true } })
  const [importBudget] = useMutation(Mutation.IMPORT_COST_BUDGET, { context: { handlesErrors: true } })
  const [upload, { uploads }] = useUpload()
  // One file at a time: take the single live progress entry (useUpload's own `progress` is not updated mid-transfer).
  const uploadProgress = (Object.values(uploads)[0] as { progress?: number } | undefined)?.progress ?? 0
  const busy = phase === 'uploading' || phase === 'checking' || phase === 'applying' || discarding

  useEffect(() => {
    if (open) { setFile(null); setFileId(null); setDoc(null); setPickerOpen(false); setPhase('pick'); setError(null); setDragOver(false); setReport(null); setDiscarding(false) }
  }, [open])

  const discard = async (id: string | null) => {
    if (!id) return
    try { await deleteFile({ variables: { ids: [id] } }) } catch (e) { console.warn('Failed to clean up a cost import file', e) }
  }

  const pick = (f: File | null) => {
    setDoc(null)
    setError(f ? validateFile(f, t) : null)
    setFile(f)
  }

  const pickDocuments = (docs: PickedDocument[]) => {
    // Equality (not truthy) narrowing: strictNullChecks is off in this project, and a plain
    // `picked.ok ? … : picked.problem` loses the discriminated-union narrowing in the else branch.
    const picked = importDocumentPick(docs)
    setFile(null)
    setDoc(picked.ok === true ? { revisionId: picked.revisionId, name: picked.name, version: picked.version } : null)
    setError(picked.ok === false ? t(picked.problem) : null)
  }
  /** The source both the dry run and the apply send: our own upload, or the Doc Control revision picked. */
  const source = (): Record<string, string> | null => (fileId ? { fileId } : doc ? { docRevisionId: doc.revisionId } : null)

  const check = async () => {
    if (doc && !error) {
      try {
        setPhase('checking')
        const res = await importBudget({ variables: { projectId, docRevisionId: doc.revisionId, dryRun: true } })
        setReport(res.data.importCostBudget)
        setPhase('report')
      } catch (e: any) {
        setError(describeCostControlError(e?.message, t))
        setPhase('pick')
      }
      return
    }
    if (!file || error) return
    let createdId: string | null = null
    try {
      setPhase('uploading')
      const { data } = await createFile({ variables: { input: { filename: file.name, size: file.size, mimetype: mimeOf(file), encoding: 'utf8', directory: 'cost-control' } } })
      createdId = data.createFile.file.id as string
      // The URL is signed for mimeOf(file); an empty file.type would make the upload send another type and fail.
      const body = file.type ? file : new File([file], file.name, { type: mimeOf(file) })
      const uploaded = await upload([{ file: body, id: createdId, name: file.name, signedUrl: data.createFile.uploadUrl }])
      if (!Array.isArray(uploaded)) {
        await discard(createdId)
        setPhase('pick')
        return
      }
      await setFileUploaded({ variables: { ids: uploaded } })
      setPhase('checking')
      const res = await importBudget({ variables: { projectId, fileId: createdId, dryRun: true } })
      setFileId(createdId)
      setReport(res.data.importCostBudget)
      setPhase('report')
    } catch (e: any) {
      await discard(createdId)
      setError(describeCostControlError(e?.message, t))
      setPhase('pick')
    }
  }

  const apply = async () => {
    const src = source()
    if (!src) return
    setError(null)
    setPhase('applying')
    let result: CostImportResult
    try {
      const res = await importBudget({ variables: { projectId, ...src, dryRun: false } })
      result = res.data.importCostBudget
    } catch (e: any) {
      setError(describeCostControlError(e?.message, t))
      setPhase('report')
      return
    }
    setReport(result)
    if (!result.applied) {
      setPhase('report')
      return
    }
    setFileId(null) // now referenced by the import's history entry: keep it
    // Stay busy until the grid shows the imported rows; a failed reload does not undo the written import.
    try {
      await client.query({ query: Query.COST_CONTROL, variables: { projectId }, fetchPolicy: 'network-only', context: { handlesErrors: true } })
    } catch (e) {
      console.warn('Failed to reload the budget after an import', e)
      toastr.warning(t('Common_error'), t('CostControl_error_reload'))
    }
    setPhase('done')
  }

  const pickOther = async () => {
    setDiscarding(true)
    try {
      await discard(fileId)
      setFileId(null); setFile(null); setDoc(null); setReport(null); setError(null); setPhase('pick')
    } finally {
      setDiscarding(false)
    }
  }

  const close = () => {
    if (busy) return
    void discard(fileId)
    onOpenChange(false)
  }

  const errors = report?.errors ?? []
  const stats = report ? [
    { label: t('CostControl_import_groups_created'), value: report.groupsCreated },
    { label: t('CostControl_import_items_created'), value: report.itemsCreated },
    { label: t('CostControl_import_items_updated'), value: report.itemsUpdated },
    { label: t('CostControl_import_unchanged'), value: report.unchanged },
  ] : []

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) close() }}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileUp className="h-5 w-5 text-primary" />{t('CostControl_import_title')}</DialogTitle>
        </DialogHeader>

        {(phase === 'pick' || phase === 'uploading' || phase === 'checking') && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{t('CostControl_import_hint')}</p>
            {locked && <p className="text-sm text-muted-foreground">{t('CostControl_import_locked_hint')}</p>}
            <div
              className={cn('cursor-pointer rounded-md border-2 border-dashed p-6 text-center transition-colors', dragOver ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50', busy && 'pointer-events-none opacity-60')}
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); pick(e.dataTransfer.files?.[0] ?? null) }}
            >
              <input ref={inputRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={(e) => { pick(e.target.files?.[0] ?? null); e.target.value = '' }} />
              {file ? <div className="text-sm font-medium">{file.name}</div>
                : doc ? (
                  <div className="flex items-center justify-center gap-2 text-sm font-medium">
                    {doc.name}
                    <Badge variant="secondary">{t('CostControl_attachment_doc_badge', { version: doc.version ?? '' })}</Badge>
                  </div>
                ) : <div className="text-sm text-muted-foreground">{t('CostControl_import_drop_hint')}</div>}
            </div>
            {canLinkDocuments && (
              <Button variant="outline" size="sm" className="h-8" onClick={() => setPickerOpen(true)} disabled={busy}>
                <FolderKanban className="h-4 w-4" />
                {t('CostControl_import_from_doc_control')}
              </Button>
            )}
            {phase === 'uploading' && <Progress value={uploadProgress} />}
            {phase === 'checking' && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground"><LoaderCircle className="h-4 w-4 animate-spin" />{t('CostControl_import_checking')}</div>
            )}
            {error && <p className="flex items-start gap-2 text-sm text-destructive"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{error}</p>}
            <div className="flex items-center gap-2 pt-4 border-t">
              <Button variant="link" className="h-8 px-0" onClick={() => window.open(costBudgetDownloadUrl(projectId, 'template'), '_blank')}>
                <Download className="h-4 w-4" />
                {t('CostControl_import_template')}
              </Button>
              <div className="flex-1" />
              <Button variant="outline" onClick={close} disabled={busy}>{t('Common_cancel')}</Button>
              <Button onClick={() => { void check() }} disabled={!(file || doc) || !!error || busy}>
                {busy && <LoaderCircle className="h-4 w-4 animate-spin" />}
                {t('CostControl_import_check')}
              </Button>
            </div>
          </div>
        )}

        {(phase === 'report' || phase === 'applying' || phase === 'done') && report && (
          <div className="space-y-4">
            {phase === 'done' && <div className="flex items-center gap-2 text-sm"><CheckCircle2 className="h-5 w-5 text-success" />{t('CostControl_import_done')}</div>}
            <div className="grid grid-cols-4 gap-3 text-center">
              {stats.map(({ label, value }) => (
                <div key={label} className="rounded-md border p-3">
                  <div className="text-2xl font-bold tabular-nums">{value}</div>
                  <div className="text-xs text-muted-foreground">{label}</div>
                </div>
              ))}
            </div>
            {errors.length > 0 && (
              <div className="space-y-2">
                <p className="flex items-start gap-2 text-sm text-destructive"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{t('CostControl_import_has_errors')}</p>
                <ImportErrorsTable errors={errors} />
              </div>
            )}
            {errors.length === 0 && phase === 'report' && !hasChanges(report) && <p className="text-sm text-muted-foreground">{t('CostControl_import_no_changes')}</p>}
            {error && <p className="flex items-start gap-2 text-sm text-destructive"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{error}</p>}
            <div className="flex items-center justify-end gap-2 pt-4 border-t">
              {phase === 'done' ? (
                <Button onClick={() => onOpenChange(false)}>{t('Common_close')}</Button>
              ) : (
                <>
                  <Button variant="outline" onClick={() => { void pickOther() }} disabled={busy}>
                    {discarding && <LoaderCircle className="h-4 w-4 animate-spin" />}
                    {t('CostControl_import_pick_other')}
                  </Button>
                  <div className="flex-1" />
                  <Button variant="outline" onClick={close} disabled={busy}>{t('Common_cancel')}</Button>
                  <Button onClick={() => { void apply() }} disabled={busy || errors.length > 0 || !hasChanges(report)}>
                    {phase === 'applying' && <LoaderCircle className="h-4 w-4 animate-spin" />}
                    {t('CostControl_import_apply')}
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
        {canLinkDocuments && (
          <DocumentSelectorModal isOpen={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={(docs) => pickDocuments(docs as PickedDocument[])} projectId={projectId} multiple={false} />
        )}
      </DialogContent>
    </Dialog>
  )
}
export default ImportDialog
